import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import '../../polyfills/polyfill-allSettled';
import ContainerWrapper from '../styled components/ContainerWrapper';
import Sticky from '../styled components/Sticky';
import Button from '../components/Button/Button';
import {
  deprecatedBEDateFormat,
  getWeekDaysBetween,
  getWeekOf,
  todayDateObject
} from '../libs/dateTimeUtilities';
import { printInInputDateFormat } from '../libs/dateTimePrinting';
import { dateFormatOptions } from '../libs/dateFormatting';
import SearchField from '../components/SearchField/SearchField';
import EventsSearchResults from '../components/EventsSearchResults';
import LoadingState from '../styled components/LoadingState';
import FormField from '../components/FormField/FormField';
import Divider from '../styled components/Divider';
import FeatureFlagWrapper from '../components/FeatureFlagWrapper';

import {
  weekEventsDay1,
  weekEventsDay2,
  weekEventsDay3,
  weekEventsDay4,
  weekEventsDay5,
  weekEventsDay6,
  weekEventsDay7
} from '../../cypress/testData/testEvents';
import FormatSettingsContext from '../components/FormatSettingsContext';
import NavigationHistory from '../libs/NavigationHistory';

/* NOTE: The animation when navigating to this widget does not work
  because an input element inside the widget (the search input) has the prop autofocus.
  This is a known bug inside Framer motion https://github.com/framer/motion/issues/1432
*/
class EventsSearchContainer extends React.Component {
  constructor(props) {
    super(props);

    const {
      user,
      headerRef,
      initialEventsAreLoaded,
      initialIsSearching,
      initialSearchInputValue,
      initialEvents,
      initialPerformedSearchOnce,
      dateFormatSetting
    } = props;

    this.testEvents = {
      events: [
        weekEventsDay1,
        weekEventsDay2,
        weekEventsDay3,
        weekEventsDay4,
        weekEventsDay5,
        weekEventsDay6,
        weekEventsDay7
      ]
    };
    const staticCurrentDate = todayDateObject();

    const currentDateOneYearAgo = cloneDeep(staticCurrentDate).subtract(1, 'y');
    this.afterDatePlaceholder = printInInputDateFormat(
      currentDateOneYearAgo,
      dateFormatSetting
    );

    const initialButtonDisabled =
      initialIsSearching || initialSearchInputValue.length === 0;

    this.state = {
      events: initialEvents,
      performedSearchOnce: initialPerformedSearchOnce,
      dates: getWeekDaysBetween(
        getWeekOf(staticCurrentDate).startDateObject,
        getWeekOf(staticCurrentDate).endDateObject
      ),
      headerHeight: 0,
      eventsAreLoaded: initialEventsAreLoaded,
      isSearching: initialIsSearching,
      searchInputValue: initialSearchInputValue,
      afterDateInputValue: currentDateOneYearAgo,
      searchButtonDisabled: initialButtonDisabled
    };

    this.containerWrapperRef = React.createRef();
    this.eventsWeekRef = React.createRef();

    [
      'isFeatureActive',
      'onChangeSearchField',
      'onChangeAfterDateField',
      'fetchEvents',
      'setSearchFeedback',
      'getEvents',
      'createTestEvents'
    ].forEach((eventHandler) => {
      this[eventHandler] = this[eventHandler].bind(this);
    });
  }

  componentDidMount() {
    const { headerRef } = this.props;
    if (this.state.searchInputValue.length !== 0) {
      this.setState({
        isSearching: false
      });
    }
    if (headerRef) {
      if (headerRef.current) {
        this.setState({
          headerHeight: headerRef.current.getHeaderHeight() + 1
        });
      }
    }
  }

  onChangeSearchField(value) {
    this.setState({
      searchButtonDisabled: value.length === 0,
      searchInputValue: value
    });
  }

  onChangeAfterDateField(value) {
    this.setState({
      afterDateInputValue: moment(value, deprecatedBEDateFormat)
    });
  }

  setSearchFeedback() {
    if (this.state.performedSearchOnce) {
      if (this.state.eventsAreLoaded) {
        return (
          <EventsSearchResults
            ref={this.eventsWeekRef}
            containerWrapperRef={this.containerWrapperRef}
            headerRef={this.props.headerRef}
            {...this.props}
            events={this.state.events}
            searchValue={this.state.searchInputValue}
          />
        );
      }
      if (this.state.isSearching) {
        return (
          <div tw="relative mt-28">
            <LoadingState $size="small" />
          </div>
        );
      }
    }
    return <></>;
  }

  getEvents() {
    this.state.dates.map((date) => this.fetchEvents(date));

    this.setState({
      isSearching: true,
      searchButtonDisabled: true,
      performedSearchOnce: true,
      eventsAreLoaded: false
    });

    setTimeout(() => {
      this.setState({
        isSearching: false,
        searchButtonDisabled: false,
        eventsAreLoaded: true
      });
    }, 1500);
  }

  createTestEvents(eventsGroupedInArrays) {
    const allEvents = [];
    eventsGroupedInArrays.forEach((eventArray) => {
      eventArray.forEach((event) => {
        allEvents.push(event);
      });
    });
    return allEvents;
  }

  async fetchEvents(dateObject) {
    const allEvents = this.createTestEvents(this.testEvents.events);

    this.setState({
      events: allEvents
    });
  }

  isFeatureActive() {
    return true;
  }

  render() {
    const { afterDateInputValue, searchButtonDisabled, headerHeight, isSearching } =
      this.state;
    const { minAfterDate, maxAfterDate } = this.props;
    return (
      <FeatureFlagWrapper isFeatureActive={this.isFeatureActive} feature="events">
        <div tw="mx-auto w-full md:max-w-screen-md">
          <Sticky
            ref={this.containerWrapperRef}
            tw="bg-silver-lighten-30"
            $zIndex={4}
            data-events-header
          >
            <div tw="">
              <ContainerWrapper
                $paddingTop={`${headerHeight}`}
                $relative
                tw="px-0"
              >
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    this.getEvents();
                  }}
                  action="."
                  tw=" mt-24 mb-8"
                >
                  <div tw="px-16">
                    <SearchField
                      onChange={this.onChangeSearchField}
                      placeholder={i18n('Search for event name')}
                      tw="mb-16 "
                      key="searchEvents"
                      data-test-events-searchfield
                    />
                  </div>

                  <div tw=" pt-8  ">
                    <FormField tw="min-w-0 flex-grow px-16">
                      <div tw="inline-block">
                        <FormField.Label $forInput="afterDate">
                          {i18n('After')}
                        </FormField.Label>
                      </div>
                    </FormField>
                    <div tw="flex justify-between items-start space-x-8 pb-8 relative">
                      <FormField tw="flex-grow block w-full pl-16">
                        <FormField.DateInput
                          onChange={this.onChangeAfterDateField}
                          id="afterDate"
                          minDate={minAfterDate}
                          maxDate={maxAfterDate}
                          placeholder={this.afterDatePlaceholder}
                          initialInputDateObject={afterDateInputValue}
                        />
                      </FormField>
                      <div tw="pr-16">
                        <Button
                          tw="mt-8 px-8 flex-shrink-0"
                          color={searchButtonDisabled ? 'default' : 'primary'}
                          variant="filled"
                          size="large"
                          disabled={searchButtonDisabled}
                          type="submit"
                          onClick={() => {
                            this.getEvents();
                          }}
                          dataAttributes={{
                            'data-test-button': 'events-search-submit'
                          }}
                        >
                          {!isSearching && i18n('Search')}
                          {isSearching && i18n('Searching ...')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
                <div tw="px-16">
                  <Divider tw="pt-8 pb-8" />
                </div>
              </ContainerWrapper>
            </div>
          </Sticky>
          <ContainerWrapper>
            <div tw="pt-8 pb-8" />
          </ContainerWrapper>
          <ContainerWrapper $relative>{this.setSearchFeedback()}</ContainerWrapper>
        </div>
      </FeatureFlagWrapper>
    );
  }
}

EventsSearchContainer.propTypes = {
  user: PropTypes.shape({
    host: PropTypes.string,
    id: PropTypes.string,
    preferences: PropTypes.shape({
      eventsFiltersApp: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  initialSearchInputValue: PropTypes.string,
  initialIsSearching: PropTypes.bool,
  initialEventsAreLoaded: PropTypes.bool,
  initialPerformedSearchOnce: PropTypes.bool,
  initialEvents: PropTypes.arrayOf(PropTypes.shape({})),
  currentDate: PropTypes.shape({}),
  minAfterDate: PropTypes.shape({
    toDate: PropTypes.func.isRequired
  }),
  maxAfterDate: PropTypes.shape({
    toDate: PropTypes.func.isRequired
  }),
  eventsFilterOptions: PropTypes.shape({}),
  fetchEventFilterOptions: PropTypes.func.isRequired,
  dateFormatSetting: PropTypes.oneOf(dateFormatOptions).isRequired
};

EventsSearchContainer.defaultProps = {
  initialEventsAreLoaded: false,
  initialIsSearching: false,
  initialPerformedSearchOnce: false,
  initialSearchInputValue: '',
  initialEvents: [],
  currentDate: todayDateObject(),
  minAfterDate: todayDateObject().subtract(3, 'y'),
  maxAfterDate: todayDateObject().add(10, 'y'),
  eventsFilterOptions: null
};

function ForwardRef(props, ref) {
  return (
    <FormatSettingsContext.Consumer>
      {(contextProps) => (
        <EventsSearchContainer {...props} {...contextProps} headerRef={ref} />
      )}
    </FormatSettingsContext.Consumer>
  );
}
export default React.forwardRef(ForwardRef);
